import { Color3, Color4, InstancedMesh, Mesh, PBRMetallicRoughnessMaterial, PolygonMeshBuilder, Ray, Scene, ShadowGenerator, Vector2, Vector3 } from "@babylonjs/core";
import { Point3 } from "../models/point.type";
import { Polygon } from "../models/polygon";
import { ModelLoaderService } from "./model-loader.service";
import earcut from "earcut";

export interface ContextMeshMetadata<T> {
    id: number,
    polygon?: Polygon,
    coordinates?: number[][],
    type: string,
    visible: boolean,
    data: T
}
export class ContextService {

    private colorHiddenContext = new Color3(0.5, 0, 1);
    private contextGeom?: Mesh;

    private modelLoaderService: ModelLoaderService;
    private shadowGenerator: ShadowGenerator;
    private scene: Scene;

    hoverEnabled = false;

    constructor(modelLoader: ModelLoaderService, scene: Scene, shadowGenerator: ShadowGenerator) {
        this.modelLoaderService = modelLoader;
        this.scene = scene;
        this.shadowGenerator = shadowGenerator;
    }

    cleanExtrusion() {
        if (this.contextGeom) {
            this.contextGeom.dispose();
            this.contextGeom = undefined;
        }
    }

    createPolygonExtrusionContextItem<T>(p: { type: string, coordinates: number[][], holes: number[][][], id: string | number, height: number, offsetFromGround: number, color: number[], visible: boolean, alpha?: number, edges?: { color: number[], width: number }, data?: T }) {
        this.initContextIfNotInitiated();
        let polygonMesh = this.createPolygonExtrusion(p);
        let pbr = new PBRMetallicRoughnessMaterial("pbrex" + p.id + Math.random(), this.scene);
        pbr.baseColor = new Color3(p.color[0], p.color[1], p.color[2]);
        pbr.metallic = 0.3;
        pbr.roughness = 0.8;
        pbr._environmentIntensity = 1;
        pbr.backFaceCulling = false;
        pbr._specularIntensity = 0;

        if (p.alpha !== undefined) {
            pbr.alpha = p.alpha;
        }

        polygonMesh.material = pbr;

        polygonMesh.metadata = {
            id: p.id,
            color: p.color,
            type: p.type,
            visible: p.visible,
            polygon: { path: p.coordinates, holes: p.holes },
            data: p.data
        } as ContextMeshMetadata<T>;
        polygonMesh.parent = this.contextGeom;
        polygonMesh.isPickable = false;
        polygonMesh.isVisible = p.visible;
        polygonMesh.receiveShadows = true;

        if (p.edges) {
            polygonMesh.enableEdgesRendering();
            polygonMesh.edgesRenderer.lineShader.options.useClipPlane = null;
            polygonMesh.edgesWidth = p.edges.width;
            polygonMesh.edgesColor = new Color4(p.edges.color[0], p.edges.color[1], p.edges.color[2]);
        }

        this.shadowGenerator.addShadowCaster(polygonMesh);
    }

    createInstanceContextItem<T>(path, fileName, p: { type: string, coordinate: Point3, id: string | number, visible: boolean, data: T }) {
        this.initContextIfNotInitiated();
        return this.modelLoaderService.loadObjToScene(fileName, path, 0.5, undefined, true).then(cachedMesh => {
            const children = cachedMesh.mesh.getChildMeshes();
            cachedMesh.mesh.receiveShadows = true;

            let instance = cachedMesh.mesh.createInstance('context-instance-parent-' + p.id);
            if (children && children.length > 0) {
                instance.isVisible = false;
                children.forEach((child, index) => {
                    const childInstance = (child as Mesh).createInstance('context-instance-' + p.id);
                    childInstance.parent = instance;
                    childInstance.metadata = { id: p.id, type: p.type, visible: p.visible };
                    childInstance.isVisible = p.visible;

                    if (child.material) {
                        const mat = child.material as PBRMetallicRoughnessMaterial;
                        mat.roughness = 1;
                        mat.metallic = 0;
                    }
                })
            }
            this.shadowGenerator.addShadowCaster(instance, true);
            instance.parent = this.contextGeom;
            instance.metadata = { id: p.id, type: p.type, coordinates: [p.coordinate], data: p.data, visible: p.visible, } as ContextMeshMetadata<T>;
            instance.position = new Vector3(p.coordinate[0], p.coordinate[1], p.coordinate[2])
        });
    }

    checkIntersectionWithContext(ray: Ray, pickedContextItemCallback: (res: { id: number }) => void) {
        if (this.contextGeom) {
            const pickInfo = ray.intersectsMeshes(
                this.contextGeom.getChildMeshes() as any
            );
            if (pickInfo.length > 0) {
                let pid = { id: -1 };
                for (let i = 0; i < pickInfo.length; i++) {
                    const p = pickInfo[i];
                    if (p.pickedMesh.isVisible) {
                        pid = p.pickedMesh.metadata;
                        break;
                    }
                }
                if (pid.id !== -1) {
                    pickedContextItemCallback(pid);
                }
            }
        }
    }

    checkHoverIntersectionWithContext(ray: Ray, hoverContextItemCallback: (id: number) => void) {
        if (this.contextGeom && this.hoverEnabled) {
            const pickInfo = ray.intersectsMeshes(
                this.contextGeom.getChildMeshes() as any
            );
            if (pickInfo.length > 0) {
                let pid = -1;
                for (let i = 0; i < pickInfo.length; i++) {
                    const p = pickInfo[i];
                    if (p.pickedMesh.isVisible) {
                        pid = p.pickedMesh.metadata;
                        break;
                    }
                }
                if (pid !== -1) {
                    hoverContextItemCallback(pid);
                }
            }
        }
    }

    //#region HIDE/UNHIDE Context Items

    setMetadataToInvisible(id: number) {
        const children = this.contextGeom.getChildMeshes();
        const childrenWithId = children.filter(c => c.metadata.id === id);
        childrenWithId.forEach(c => {
            c.metadata.visible = false;
        })
    }

    setMetadataToVisible(id: number) {
        const children = this.contextGeom.getChildMeshes();
        const childrenWithId = children.filter(c => c.metadata.id === id);
        childrenWithId.forEach(c => {
            c.metadata.visible = true;
        })
    }

    setToInvisibleThroughAlphaToContextItem(id: string | number, alpha: number) {
        const children = this.contextGeom.getChildMeshes();
        const childrenWithId = children.filter(c => c.metadata.id === id);
        childrenWithId.forEach(c => {

            if (c instanceof Mesh) {
                if (c.material) {
                    if (c.material instanceof PBRMetallicRoughnessMaterial) {
                        c.material.baseColor = this.colorHiddenContext;
                    }
                    c.material.alpha = alpha;
                    c.metadata.visible = false;
                }
            } else if (c instanceof InstancedMesh) {
                c.showBoundingBox = true;
                c.metadata.visible = false;
            }
        })
    }

    getContextMeshMetadata<T>(id: string | number): ContextMeshMetadata<T>[] {
        const children = this.contextGeom.getChildMeshes();
        const childrenWithId = children.filter(c => c.metadata.id === id);
        return childrenWithId.map(c => c.metadata)
    }

    setAlphaToContextMesh(id: string | number, alpha: number) {
        const children = this.contextGeom.getChildMeshes();
        const childrenWithId = children.filter(c => c.metadata.id === id);
        childrenWithId.forEach(c => {
            if (c instanceof Mesh) {
                if (c.material) {
                    c.material.alpha = alpha;
                }
            }
        })
    }

    showContextMesh(id: string | number) {
        const children = this.contextGeom.getChildMeshes();
        const childrenWithId = children.filter(c => c.metadata.id === id);
        childrenWithId.forEach(c => {
            c.isVisible = true;
        })
    }


    setAlphaToContextMeshes(types: string[], alpha: number) {
        const children = this.contextGeom.getChildMeshes();
        const childrenOfType = children.filter(c => types.some(type => type === c.metadata.type && c.material.alpha !== alpha));

        childrenOfType.forEach(c => {
            if (c instanceof Mesh) {
                if (c.material) {
                    c.material.alpha = alpha;
                }
            }
        })
    }

    makeContextItemVisible(id: string | number) {
        const children = this.contextGeom.getChildMeshes();
        const chilrenWithId = children.filter(c => c.metadata.id === id);
        chilrenWithId.forEach(c => {
            if (c.material) {
                c.material.alpha = 1;
                c.metadata.visible = true;
                if (c.material instanceof PBRMetallicRoughnessMaterial) {
                    c.material.baseColor = new Color3(c.metadata.color[0], c.metadata.color[1], c.metadata.color[2]);
                }
            }
            c.isVisible = true;
            c.showBoundingBox = false;
        })
    }

    setAlphaContextItemsToInvisible(types: string[]) {
        const children = this.contextGeom.getChildMeshes();
        children.filter(c => types.some(t => t === c.metadata.type)).forEach(child => {
            if (child instanceof Mesh) {
                if (child.material.alpha !== 1) {
                    child.isVisible = false;
                    child.isPickable = false;
                }
            } else if (child instanceof InstancedMesh) {
                if (child.showBoundingBox) {
                    child.isVisible = false;
                    child.isPickable = false;
                    child.showBoundingBox = false;
                }
            }
        })
    }

    hideContextItem(id: string | number) {
        const children = this.contextGeom.getChildMeshes();
        const childs = children.filter(i => i.metadata.id === id);
        childs.forEach(s => s.isVisible = false);
    }

    hideContextItems(ids: string[] | number[]) {
        const children = this.contextGeom.getChildMeshes();
        ids.forEach(id => {
            const childs = children.filter(i => i.metadata.id === id);
            childs.forEach(s => s.isVisible = false);
        });
    }

    showContextItems(types: string[]) {
        const children = this.contextGeom.getChildMeshes();
        children.filter(c => types.some(t => t === c.metadata.type)).forEach(child => {
            if (child.metadata.visible) {
                child.isVisible = true;
            }
        })
    }

    startHidingContextItems(types: string[]) {
        const children = this.contextGeom.getChildMeshes();
        children.filter(c => types.some(t => t === c.metadata.type)).forEach(child => {
            if (child instanceof Mesh) {
                if (!child.isVisible) {
                    child.isPickable = true;
                    child.isVisible = true;
                    child.material.alpha = 0.2;

                    if (child.material instanceof PBRMetallicRoughnessMaterial) {
                        child.material.baseColor = this.colorHiddenContext;
                    }
                }
            } else if (child instanceof InstancedMesh) {
                if (!child.isVisible) {
                    child.isVisible = true;
                    child.showBoundingBox = true;
                }
            }
        })
    }

    //#endregion

    private initContextIfNotInitiated() {
        if (!this.contextGeom) {
            this.contextGeom = new Mesh('context', this.scene);
            this.contextGeom.rotate(new Vector3(0, 1, 0), -Math.PI / 2);
        }
    }

    private createPolygonExtrusion(p: { coordinates: number[][], holes: number[][][], id: string | number, height: number, offsetFromGround: number, color: number[], visible: boolean, edges?: any }): Mesh {
        const height = p.height;
        let name = 'extrusion' + p.id + Math.random();
        if (p.edges) {
            name = 'edges-extrusion' + p.id + Math.random();
        }
        const polBuilder = new PolygonMeshBuilder(name, p.coordinates.map(p => new Vector2(p[0], p[1])), this.scene, earcut);
        p.holes.forEach(hole => {
            polBuilder.addHole(hole.map(p => new Vector2(p[0], p[1])));
        })
        let polygon = polBuilder.build(false, height);
        polygon.translate(new Vector3(0, 1, 0), height + p.offsetFromGround);
        return polygon;
    }

    hideContextTypes(types: string[]) {
        const children = this.contextGeom.getChildMeshes();
        const childrenOfType = children.filter(c => types.some(type => c.metadata.type === type));
        childrenOfType.forEach(c => {
            c.isVisible = false;
            c.isPickable = false;
        });
    }

    showContextTypes(types: string[], alpha = 1, enableEdge = false) {
        const children = this.contextGeom.getChildMeshes();
        const childrenOfType = children.filter(c => types.some(type => c.metadata.type === type));
        childrenOfType.forEach(c => {
            c.isVisible = true;
            c.isPickable = true;
            if (c.material) {
                c.material.alpha = alpha;
            }
            if (enableEdge) {
                c.enableEdgesRendering();
            }
        });
    }
}